import { useRef } from "react"
import styled from "@emotion/styled"
import { motion, useScroll, useInView } from "framer-motion"
import parse from "html-react-parser"

import { slideUp } from "animations/slideUp"

import { fullGrid } from "css/grid"
import { headline70, body50 } from "css/text"
import getMediaQuery from "css/breakpoints"

import Date from "components/text/Date"
import Image from "components/media/ImageCLD"
import PopinButton from "components/popin-edito/PopinButton"
import { useConsole } from "contexts/Console"
import SpecTermBtn from "components/quickviews/specific-terms/SpecTermBtn"

const Root = styled(motion.section)`
  ${fullGrid}
  grid-template-rows: [top-row] 85vw auto [bottom-row];
  position: relative;
  background: white;
  overflow: hidden;
  transform: translate3d(0, 0, 0);

  ${getMediaQuery("m")} {
    grid-template-rows: [top-row] auto [bottom-row];
    height: 300vh;
    overflow: unset;
  }
`

const TxtBloc = styled(motion.div)`
  grid-column: main;
  grid-row: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;

  position: sticky;
  z-index: 2;
  top: 0;
  align-self: end;

  ${getMediaQuery("m")} {
    grid-row: top-row / bottom-row;
    height: 100vh;
    align-self: start;
    grid-column: col 4 / span 4;
  }

  button:not(.spec-term-btn) {
    margin-block-start: 1.875rem;
  }
`

const Title = styled.h2`
  ${headline70}
  margin-block-end: 20px;
  & em {
    font-style: normal;
    color: rgb(var(--green));
  }
`

const Paragraph = styled.p`
  ${body50}
`

const BezelContainer = styled.div`
  --xfactor: calc(clamp(0, var(--prog), 0.4) / 0.4);
  --dirfactor: 1;
  [dir="rtl"] & {
    --dirfactor: -1;
  }

  display: flex;
  align-items: start;
  grid-column: doc-start / doc-end;
  grid-row: 1;
  width: 100%;
  align-self: center;

  & img {
    max-width: unset;
  }

  padding-block-start: 5vh;

  & figure img:not(.shadow) {
    transform: translate(-50%, -50%);
  }

  &.rotate-active {
    & figure:nth-of-type(-n + 2) {
      & img:not(.shadow) {
        transform: translate(-50%, -50%) rotate(calc((-90 + 90 * var(--rotatefactor)) * (1deg * var(--dirfactor))));
      }
    }
    & figure:nth-last-of-type(-n + 2) {
      & img:not(.shadow) {
        transform: translate(-50%, -50%) rotate(calc((90 + -90 * var(--rotatefactor)) * (1deg * var(--dirfactor))));
      }
    }
  }

  & figure {
    --sxy: calc(2 + -1 * var(--scalefactor)), calc(2 + -1 * var(--scalefactor));
  }

  & figure:nth-of-type(1) {
    display: none;
  }
  & figure:nth-of-type(2) {
    inset-inline-start: 35%;
    transform: translate3d(calc((-70vw * var(--dirfactor)) + (70vw * var(--dirfactor)) * var(--xfactor)), 0, 0) scale3d(var(--sxy), 1);
  }
  & figure:nth-last-of-type(1) {
    display: none;
  }
  & figure:nth-last-of-type(2) {
    inset-inline-end: 0%;
    inset-inline-start: unset;
    transform: translate3d(calc((75vw * var(--dirfactor)) + (-75vw * var(--dirfactor)) * var(--xfactor)), 0, 0) scale3d(var(--sxy), 1);
  }

  ${getMediaQuery("m")} {
    --xfactor: calc(clamp(0, var(--prog), 0.75) / 0.75);

    position: sticky;
    top: 0;
    grid-row: top-row / bottom-row;
    height: 100vh;
    align-items: center;
    align-self: start;
    padding-block-start: unset;
    overflow: hidden;

    & figure {
      --sxy: calc((2 + -1.6 * var(--scalefactor))), calc((2 + -1.6 * var(--scalefactor)));
    }

    & figure:nth-of-type(1) {
      inset-inline-start: 0%;
      display: block;
      transform: translateX(calc((-95vw * var(--dirfactor)) + (95vw * var(--dirfactor)) * var(--xfactor))) scale3d(var(--sxy), 1);
    }
    & figure:nth-of-type(2) {
      inset-inline-start: 18%;
      display: block;
      inset-inline-end: unset;
      transform: translateX(calc((-35vw * var(--dirfactor)) + (35vw * var(--dirfactor)) * var(--xfactor))) scale3d(var(--sxy), 1);
    }
    & figure:nth-last-of-type(1) {
      inset-inline-end: 12%;
      display: block;
      transform: translateX(calc((145vw * var(--dirfactor)) + (-145vw * var(--dirfactor)) * var(--xfactor))) scale3d(var(--sxy), 1);
    }
    & figure:nth-last-of-type(2) {
      inset-inline-end: 30%;
      display: block;
      transform: translateX(calc((80vw * var(--dirfactor)) + (-80vw * var(--dirfactor)) * var(--xfactor))) scale3d(var(--sxy), 1);
    }
  }
`

const Figure = styled(motion.figure)`
  --scalefactor: calc(clamp(0, var(--prog), 0.35) / 0.35);

  .rotate-active & {
    --rotatefactor: calc(clamp(0.3, var(--prog), 1) / 1);
  }

  position: absolute;
  transform-origin: center;
  display: block;
  z-index: 1;

  ${getMediaQuery("m")} {
    --scalefactor: calc(clamp(0, var(--prog), 0.75) / 0.75);
    .rotate-active & {
      --rotatefactor: calc(clamp(0.3, var(--prog), 1) / 1);
    }
  }

  & img:not(.shadow) {
    position: absolute;
    left: 50%;
    top: 50%;
    display: block;
    width: 55vw;
    height: auto;
    transform: translate3d(0, 0, 0);
    ${getMediaQuery("m")} {
      width: 40vw;
    }
  }
`

const Shadow = styled.div`
  position: absolute;
  transform-origin: center;
  z-index: 0;
  transform: translate(-50%, -50%);
  opacity: calc((0.5 + 0.5 * var(--prog)));
  [dir="rtl"] & {
    transform: translate(50%, -50%);
  }

  & img {
    display: block;
    position: relative;
    transform: translate3d(calc((10 * (var(--prog) - 1) - 2) * 1%), calc((-10 * (var(--prog) - 1) + 2) * 1%), 0);
    width: 65vw;
    height: 65vw;

    [dir="rtl"] & {
      transform: translate3d(calc((-10 * (var(--prog) - 1) - 2) * 1%), calc((-10 * (var(--prog) - 1) + 2) * 1%), 0);
    }

    ${getMediaQuery("m")} {
      width: 50vw;
      height: 50vw;
    }
  }
`

function Bezel({ image, shadow }) {
  return (
    <Figure>
      {!!shadow[0]?.sources?.length ? (
        <Shadow>
          <Image {...shadow[0]} className='shadow' sizes='35vw' />
        </Shadow>
      ) : null}
      <Image {...image} sizes='35vw' />
    </Figure>
  )
}

export default function IntroFeaturesBezel({
  heading,
  paragraph,
  date,
  bezels_cld: bezels,
  rotation,
  shadow_cld: shadow,
  popin_button,
  box_spacing,
  ...props
}) {
  const ref = useRef()
  const textRef = useRef()
  const prefersReducedMotion = process.browser && document.documentElement.classList.contains("prefers-reduced-motion")
  const console = useConsole()

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] })
  const isInView = useInView(textRef, { margin: "100% 0% 0% 0%" })

  console.log("\n•••••• paragraph", paragraph)

  const transformHtml = html => {
    return parse(html, {
      replace: node => {
        console.log("••••• node", node)
        if (node.name === "spec-term") {
          const content = node.children[0].data
          return <SpecTermBtn content={content} {...node.attribs} />
        }
      },
    })
  }

  const hhh = transformHtml(paragraph)

  console.log("•••••• hhh", hhh)

  return (
    <Root
      ref={ref}
      style={{ "--prog": scrollYProgress }}
      className={[box_spacing?.padding_top, box_spacing?.padding_bottom, box_spacing?.margin_top, box_spacing?.margin_bottom].join(" ")}
    >
      <TxtBloc ref={textRef} variants={slideUp} initial='hidden' animate={prefersReducedMotion ? "show" : isInView ? "show" : "hide"}>
        {date && <Date {...date} />}
        <Title dangerouslySetInnerHTML={{ __html: heading }} />
        {/* <Paragraph dangerouslySetInnerHTML={{ __html: paragraph }} /> */}
        <Paragraph>{transformHtml(paragraph)}</Paragraph>
        {popin_button?.reference && <PopinButton {...popin_button} />}
      </TxtBloc>
      <BezelContainer className={!!rotation ? "rotate-active" : ""}>
        {bezels?.map((image, i) => (
          <Bezel key={`bezel-${i}`} image={image} shadow={shadow} />
        ))}
      </BezelContainer>
    </Root>
  )
}
